import React, { memo } from "react";
import {
  Box,
  Divider,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import UserAvatar from "modules/_shared/user-avatar";
import colors from "app/colors";
import UserName from "modules/_shared/user-name";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { CloseIcon, TranslateIcon } from "modules/icons";
import { IQuotedMessage } from "modules/providers/index-db-provider/index-db-models";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
  QuotedMessageFragment,
} from "gql/graphql";
import FileThumb from "modules/files/file-thumb";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import QuotedMessageFileTypeIcon from "modules/files/quoted-message-file-type-icon";
import useMessageText from "modules/hooks/use-message-text";
import MessageText from "./message-text";

interface IProps {
  messageThread?: MessageThreadFragment;
  quotedMessage: QuotedMessageFragment | IQuotedMessage;
  onClose?: () => void;
  maxLines?: number;
}

const QuotedMessage = ({
  quotedMessage,
  onClose,
  messageThread,
  maxLines,
}: IProps) => {
  // TODO: Refactor to use quotedMessage fragment only instead mashing up a new object

  const message: MessageFragment = {
    updateTime: null,
    deleted: false,
    editTime: null,
    type: MessageType.User,
    isSeen: true,
    sentTime: undefined,
    seenUserIdList: [],
    relatedMessageThreadList: [],
    fileList: [],
    gifList: [],
    reactionList: [],
    quotedMessageList: [],
    ...quotedMessage,
    __typename: "Message",
  };

  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const { messageHasContent, renderedText } = useMessageText(
    message,
    messageThread,
  );

  return (
    // Needs two levels of Flex for the vertical Divider to show up... seems hacky
    <Flex>
      <Flex>
        <Divider orientation="vertical" borderColor={colors.gray30} />
      </Flex>
      <Stack ml={2} spacing={0.5} w="full">
        <HStack spacing={1}>
          <UserAvatar
            userId={quotedMessage.createdUserId}
            displayOnlineIndicator={false}
            size="2xs"
          />
          <Text
            noOfLines={1}
            fontSize={12}
            fontWeight={600}
            color={colors.gray50}
            flex={1}
          >
            <UserName userId={quotedMessage.createdUserId} />
          </Text>
          {onClose && (
            <CircularIconButton
              aria-label="Remove message"
              icon={<CloseIcon fill={colors.grayMain} boxSize={4} />}
              minW={4}
              h={4}
              onClick={onClose}
            />
          )}
        </HStack>
        <HStack w="full" spacing={1}>
          <VStack w="full" spacing={1} alignItems="flex-start">
            {quotedMessage.file && (
              <HStack spacing={1}>
                <QuotedMessageFileTypeIcon
                  contentType={quotedMessage.file.contentType}
                  size={[16, 16]}
                />
                <Text fontSize={12} fontWeight={700} color={colors.gray50}>
                  {quotedMessage.file.name}
                </Text>
              </HStack>
            )}
            <HStack spacing={1} alignItems="flex-start">
              {isViewingTranslatedMessage && (
                <TranslateIcon boxSize={3} fill={colors.grayMain} />
              )}
              <Box fontWeight={400} color={colors.gray50}>
                {messageHasContent ? (
                  <MessageText
                    messageThread={messageThread}
                    message={message}
                    maxLines={maxLines}
                    style={{
                      color: colors.gray50,
                      fontSize: 12,
                    }}
                  />
                ) : (
                  <Box fontStyle="italic" color={colors.grayMain} fontSize={12}>
                    {renderedText}
                  </Box>
                )}
              </Box>
            </HStack>
          </VStack>
          {quotedMessage.file && (
            <FileThumb
              contentType={quotedMessage.file.contentType}
              name={quotedMessage.file.name}
              url={quotedMessage.file.previewCloudFrontUrl}
              size={[40, 40]}
              displayFileFooterIcon={false}
            />
          )}
        </HStack>
      </Stack>
    </Flex>
  );
};

export default memo(QuotedMessage);
